import { useDebounce } from 'ahooks';
import type { FormulaInputField } from '@/api/customFields/formulaFields.interface';
import { toDateValue } from '@/api/customFields/formulaWireUnits';
import { useEvaluateFormula } from '@/api/customFields/useFormulaEvaluation';
import { isBlankControlValue } from './calculatedControlValue';

// Long enough that a typed number is one request, short enough to feel live.
// Every formula reading a field samples it separately, so an offer form with
// twenty of them turns one keystroke into twenty requests — the wait is what
// keeps that to one round per pause in typing (Venil, QA round 2).
const SAMPLE_DELAY_MS = 700;

export interface CalculatedValueArgs {
  formula: string;
  // Whatever the form holds right now, keyed the way the config names fields.
  values: Record<string, unknown>;
  // Maps a formula token to the form field holding its value.
  fieldNameOf: (token: string) => string;
  subjectType?: string;
  // The field being calculated, so the server checks the result fits it.
  kind?: string;
  fieldId?: string | number;
  // The application this offer is for. Job, Application and Referral handles
  // resolve from it, and it is known before the offer exists.
  applicationId?: string | number;
}

// The recruiter types a number the way they read it; the wire wants digits.
const NUMERIC_KINDS = ['number', 'integer', 'decimal', 'currency', 'percent'];

// The number widget keeps the recruiter's separators in form state, and reads
// both `,` and `.` as grouping — see stripSeparators in numberFieldValidation.
// A lone dot is a decimal point, so only repeated dots are grouping.
const stripGrouping = (typed: string) => {
  const withoutCommas = typed.replace(/,/g, '');
  const dots = withoutCommas.split('.').length - 1;
  return dots > 1 ? withoutCommas.replace(/\./g, '') : withoutCommas;
};

// A date widget holds a Date; the wire wants epoch milliseconds. Everything
// else crosses as typed — the server divides a percent by 100 itself.
const toWireValue = (kind: string, value: unknown) => {
  if (kind === 'date') return toDateValue(value as string | number | Date);
  if (NUMERIC_KINDS.includes(kind) && typeof value === 'string')
    return stripGrouping(value);
  return value;
};

// The form's values, keyed by handle the way evaluate_formula reads them.
// Blanks are dropped BEFORE conversion, never after: `dayjs(undefined)` is
// the current time, so converting an empty date first both invents a value
// and re-keys the query on every render.
const collectInputs = (
  fields: FormulaInputField[],
  values: Record<string, unknown>,
  fieldNameOf: (token: string) => string
) =>
  Object.fromEntries(
    fields
      .map(
        (field) =>
          [field.handle, field.kind, values[fieldNameOf(field.token)]] as const
      )
      .filter(([, kind, value]) => !isBlankControlValue(value, kind))
      .map(([handle, kind, value]) => [handle, toWireValue(kind, value)])
  );

// The requisition the recruiter has picked. The offer form holds it as
// `requisitions_codes` where requisitions are enabled and as `req_id`
// otherwise, and the server gets it only on save — so without passing it here,
// a formula reading `req.headcount` had nothing to resolve from and asked the
// recruiter to fill a field that is not on the offer.
const selectedReqId = (values: Record<string, unknown>) => {
  const codes = values?.requisitions_codes;
  const picked = Array.isArray(codes) ? codes[0] : codes;
  const reqId = picked ?? values?.req_id;
  return typeof reqId === 'string' || typeof reqId === 'number'
    ? reqId
    : undefined;
};

// The offer's calculated value, computed by the server rather than in the
// browser. The local engine reads whatever the widget holds — a percent as 10
// where the offer stores 0.1, a date as milliseconds — so only this answers
// the number the save will write.
//
// Two queries, because the formula's fields are named by handle and only the
// server knows them: called with no inputs it answers with the fields to
// collect, and the second call supplies them. While nothing is filled in the
// two share a key, so that is one request, not two.
export const useCalculatedValue = ({
  formula,
  values,
  fieldNameOf,
  subjectType = 'ats_offer',
  kind,
  fieldId,
  applicationId,
}: CalculatedValueArgs) => {
  const target = {
    kind,
    fieldId,
    reqId: selectedReqId(values),
    applicationId,
  };
  const { data: asked } = useEvaluateFormula(subjectType, formula, {}, target);
  // The inputs the formula still has nothing for; only this side can tell.
  const missing = (asked?.inputs ?? []).filter((field) =>
    isBlankControlValue(values[fieldNameOf(field.token)], field.kind)
  );

  // Serialised so a re-render holding the same values debounces as one value
  // rather than as a new object each time.
  const supplied = JSON.stringify(
    collectInputs(asked?.inputs ?? [], values, fieldNameOf)
  );
  const settled = useDebounce(supplied, { wait: SAMPLE_DELAY_MS });

  const { data, isFetching, isError } = useEvaluateFormula(
    subjectType,
    formula,
    JSON.parse(settled),
    target
  );

  // Whether this answer is still about what the form holds. The inputs settle
  // 700ms after the last keystroke and the request takes its own time, so a
  // refusal earned by the half-typed value stayed on screen for about two
  // seconds after the number it was replaced by had arrived (Venil, #19).
  const stale = supplied !== settled || isFetching;

  // A diagnostic is a formula that cannot run here — no number to show. It is
  // also the only thing that says why the field is empty: a divisor that folds
  // to zero passes authoring and only fails here, so without this the field
  // renders blank on every offer with nothing to explain it (CORE-5489).
  //
  // Held back while a newer reading is coming: it speaks for inputs the form
  // no longer has.
  const diagnostic = stale ? undefined : data?.diagnostics?.[0];
  const failed = isError || Boolean(diagnostic);

  return {
    value: failed ? null : (data?.result ?? null),
    isFetching,
    missing,
    // Every field the formula reads, blank or not. A caller holding the
    // offer's saved `blocked_by` needs to know which of those names this
    // reading can actually speak for: one it has never heard of is simply not
    // loaded yet, and its saved verdict still stands (CORE-5621 round 2 #2).
    inputs: asked?.inputs ?? [],
    diagnostic,
  };
};
